import React, {useEffect, useState} from 'react';
import {ButtonDropdownField, ColoredIdIcon, FlexRow, SubHeading, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {accountCameFromItem, applyItemDefaults} from './ExportInvoiceModal.xeroLines';
import {formatLinePercent} from './ExportDefaultTaxRate.display';
import {getProjectIndicatorString} from '../../../forecast-app/shared/components/project-indicator/support/ProjectIndicatorLogic';
import {XeroExportLineRow, XeroExportLinesTableProps, XeroTrackingCategory} from './XeroExportLinesTable.type';
import XeroLineDropdown, {XeroLineDropdownOption} from './XeroLineDropdown';
import {LINE_DROPDOWN_MAX_PX, lineGridTemplate, widestLabels} from './XeroExportLinesLayout';
import {
	ColumnSizer,
	FieldCell,
	LineCell,
	LinesGrid,
	LinesRow,
	LinesScroller,
	Numeral,
	SizerLabel,
} from './XeroExportLinesTable.styled';

/**
 * The invoice-line table for the Export to Xero window.
 *
 * Laid out as its own grid rather than with the design system's Table, whose dropdown column draws
 * every list at a fixed width and takes only fixed column widths. Here each dropdown column is as
 * wide as its longest option up to a cap, every list is exactly as wide as its field, and no column
 * ever shrinks: when the row does not fit, only this table scrolls sideways, every column with it.
 *
 * A separate component from the QuickBooks and e-conomic line table, which the same window renders
 * for those integrations, so Xero's columns cannot change theirs. Both Xero export windows render
 * it under the Xero invoicing flag only.
 *
 * Purely props-driven, with no fetching of its own.
 */
const XeroExportLinesTable = ({
	rows,
	currency,
	accounts,
	items,
	trackingCategories,
	onRowsChange,
	idHeader = '',
	disabled,
	canRepriceLines = false,
	loading = false,
	failed = {accounts: false, items: false, trackingCategories: false},
	listsLoaded = true,
}: XeroExportLinesTableProps): JSX.Element => {
	const {formatMessage, formatNumber} = useIntl();
	const showIdColumn = idHeader !== '';
	const noneLabel = formatMessage({id: 'integrations.xero.line.none'});

	/*
	 * Fields where the user picked None, keyed `${rowId}:${column}`. Picking None clears the line's
	 * value, which on its own reads exactly like never having picked; this is what lets the field
	 * say "None" instead of asking again. Display only: the export reads the cleared value. Dropped
	 * when the lists reload, because a reload for another organisation clears the lines as well.
	 */
	const [pickedNone, setPickedNone] = useState<ReadonlySet<string>>(new Set());
	useEffect(() => setPickedNone(new Set()), [items, trackingCategories]);
	const noneKey = (row: XeroExportLineRow, column: string): string => `${row.id}:${column}`;
	const markNone = (row: XeroExportLineRow, column: string, isNone: boolean): void => {
		if (disabled) return;
		setPickedNone(previous => {
			const next = new Set(previous);
			if (isNone) next.add(noneKey(row, column));
			else next.delete(noneKey(row, column));
			return next;
		});
	};

	/*
	 * "Still loading", "could not load" and "nothing imported" are three different statements, and
	 * only the last is the empty state. Saying it while a read is in flight, or after one failed,
	 * tells the user their configuration does not exist when it may be sitting there.
	 */
	const placeholder = (didFail: boolean, selectLabel: string): string => {
		// Loading first: a read still in flight has not failed yet.
		if (loading) return formatMessage({id: 'common.loading'});
		if (didFail) return formatMessage({id: 'integrations.xero.line.load_failed'});
		return selectLabel;
	};

	// Only a finished read for this organisation that returned nothing can say nothing was imported.
	const emptyMessage = (didFail: boolean, emptyId: string): string | undefined =>
		listsLoaded && !loading && !didFail ? formatMessage({id: emptyId}) : undefined;

	// A field cannot be used while its list is loading, failed, or not yet read for this organisation.
	const fieldDisabled = (didFail: boolean): boolean => loading || didFail || !listsLoaded;

	/*
	 * Changes are applied to `rows`, the prop, so what leaves this component is the caller's own
	 * row shape.
	 */
	const updateRow = (target: XeroExportLineRow, changes: Partial<XeroExportLineRow>): void => {
		/*
		 * Every write passes through here, so this one guard is what keeps the lines unchanged while
		 * the window is exporting, whichever list the pick came from.
		 */
		if (disabled) return;
		onRowsChange(rows.map(row => (row.id === target.id ? {...row, ...changes} : row)));
	};

	const itemLabel = (row: XeroExportLineRow): string => items.find(item => item.value === row.itemId)?.name ?? '';

	const accountLabel = (row: XeroExportLineRow): string => {
		const account = accounts.find(candidate => candidate.value === row.accountId);
		return account ? `${account.code} - ${account.name}` : '';
	};

	/*
	 * The SELECTED ACCOUNT's rate, which is the authoritative one - it is what the Account column
	 * labels itself from. Reading a rate copied onto the row instead meant that a row whose
	 * account was set elsewhere (`mapInvoiceEntryToRow` writes `xeroAccount` but never
	 * `xeroTaxRate`) showed the account and no rate, so the line looked untaxed.
	 *
	 * A found account wins even when its rate is null: "this account has no rate" is an answer,
	 * and falling through to the row would show a rate the account does not carry. Only an id the
	 * list cannot resolve falls back.
	 */
	const rateFor = (row: XeroExportLineRow): number | null | undefined => {
		// A client default that applies wins over the account, whatever the account.
		if (row.appliedTaxRate !== undefined) return row.appliedTaxRate;
		const account = accounts.find(candidate => candidate.value === row.accountId);
		return account ? account.taxRate : row.taxRate;
	};

	/*
	 * An account with no rate and a row with none both render a dash, while zero is a real rate and
	 * stays distinguishable from no rate at all. Fractional rates such as 12.5 % are shown exactly.
	 */
	const formatRate = (rate: number | null | undefined): string => formatLinePercent(formatNumber, rate);

	const trackingLabel =
		(trackingCategory: XeroTrackingCategory) =>
		(row: XeroExportLineRow): string => {
			const selected = row.tracking?.[trackingCategory.value];
			return trackingCategory.options.find(option => option.value === selected)?.name ?? '';
		};

	/*
	 * Keyed on the category id, never on the option name. Two categories can legitimately carry
	 * an option of the same name - "North" under both Region and Team - and keying on the label
	 * would make choosing one silently fill the other.
	 */
	const selectTracking = (row: XeroExportLineRow, categoryId: string, optionId: string | undefined): void => {
		// No `disabled` guard here: this only ever reaches the rows through updateRow, which
		// guards. Two guards for one invariant is one place to forget when the next writer lands.
		const tracking = {...(row.tracking ?? {})};
		if (optionId === undefined) {
			delete tracking[categoryId];
		} else {
			tracking[categoryId] = optionId;
		}
		updateRow(row, {tracking});
	};

	/*
	 * A hidden copy of the column's field, once per label it can show, which sets the column's width
	 * to the widest of them up to `maxWidth`. Sizing from the full list, not from what the lines hold,
	 * is what keeps a column one width in every row. Labels are drawn from an attribute so the hidden
	 * copies are not page text, and `aria-hidden` with `visibility: hidden` keeps them out of
	 * assistive tech and the tab order.
	 */
	const columnSizer = (fixedLabels: string[], optionLabels: string[], maxWidth: number): JSX.Element => (
		<ColumnSizer $maxWidth={maxWidth} aria-hidden>
			{[...fixedLabels, ...widestLabels(optionLabels)].map((label, index) => (
				<ButtonDropdownField key={`${index}-${label}`} size={'small'} width={'max-content'}>
					<SizerLabel data-label={label} />
				</ButtonDropdownField>
			))}
		</ColumnSizer>
	);

	const itemPlaceholder = placeholder(failed.items, formatMessage({id: 'integrations.xero.line.select_item'}));
	const accountPlaceholder = placeholder(failed.accounts, formatMessage({id: 'integrations.xero.line.select_account'}));
	const trackingPlaceholder = (trackingCategory: XeroTrackingCategory): string =>
		placeholder(failed.trackingCategories, trackingCategory.name);

	const itemOptionsFor = (row: XeroExportLineRow): XeroLineDropdownOption[] => [
		{
			key: 'item-none',
			label: noneLabel,
			/*
			 * Through applyItemDefaults, not a literal: clearing the item has to put the ENTRY's price
			 * back, and the line's current price is the cleared product's. The account goes the same
			 * way when the product put it there - and stays when the user did, which is why the last
			 * argument is asked rather than assumed.
			 */
			onSelect: () => {
				updateRow(row, applyItemDefaults(undefined, accounts, row.originalUnitPrice, accountCameFromItem(row, items)));
				markNone(row, 'item', true);
			},
		},
		...items.map(item => ({
			key: `item-${item.value}`,
			label: item.name,
			/*
			 * Picking an item also fills the account Xero holds for it, and the tax that account
			 * carries. For a user who may not update the invoice the item is stripped of its price and
			 * description first, so the account and tax still fill while the line keeps its own figure
			 * and text. The last argument is the PREVIOUS product's account, read before this click
			 * replaces it, so a product naming no account still takes the last one's with it.
			 */
			onSelect: () => {
				updateRow(
					row,
					applyItemDefaults(
						canRepriceLines ? item : {...item, defaultUnitPrice: undefined, description: undefined},
						accounts,
						row.originalUnitPrice,
						accountCameFromItem(row, items)
					)
				);
				markNone(row, 'item', false);
			},
		})),
	];

	// With no products imported the list holds only the message saying so - not even None.
	const itemOptions = (row: XeroExportLineRow): XeroLineDropdownOption[] => (items.length === 0 ? [] : itemOptionsFor(row));

	/*
	 * No None: once a line has an account it can be changed to another but not cleared. The tax
	 * type travels with the account because it is what the export sends; the rate is only displayed.
	 */
	const accountOptions = (row: XeroExportLineRow): XeroLineDropdownOption[] =>
		accounts.map(account => ({
			key: `account-${account.value}`,
			label: `${account.code} - ${account.name}`,
			onSelect: () =>
				updateRow(row, {
					accountId: account.value,
					accountCode: account.code,
					// null means the account carries no rate. Undefined keeps that distinct from zero.
					taxRate: account.taxRate ?? undefined,
					taxType: account.taxType,
				}),
		}));

	const trackingOptions = (row: XeroExportLineRow, trackingCategory: XeroTrackingCategory): XeroLineDropdownOption[] => {
		const column = `tracking-${trackingCategory.value}`;
		return [
			{
				key: `tracking-none-${trackingCategory.value}`,
				label: noneLabel,
				onSelect: () => {
					selectTracking(row, trackingCategory.value, undefined);
					markNone(row, column, true);
				},
			},
			...trackingCategory.options.map(option => ({
				key: `tracking-option-${option.value}`,
				label: option.name,
				onSelect: () => {
					selectTracking(row, trackingCategory.value, option.value);
					markNone(row, column, false);
				},
			})),
		];
	};

	// The chosen value, else None when the user picked it, else nothing so the field shows its placeholder.
	const shownValue = (label: string, row: XeroExportLineRow, column: string): string | undefined =>
		label || (pickedNone.has(noneKey(row, column)) ? noneLabel : undefined);

	/*
	 * An account the loaded list cannot resolve is no longer active in Xero. Only claimed once the
	 * list has answered for this organisation: while it loads, after it failed, or before it was
	 * read, an unresolved id says nothing about the account.
	 */
	const accountInactive = (row: XeroExportLineRow): boolean =>
		!!row.accountId &&
		listsLoaded &&
		!loading &&
		!failed.accounts &&
		!accounts.some(candidate => candidate.value === row.accountId);

	const money = (value: number | undefined, maximumFractionDigits = 2): string =>
		value === undefined || value === null
			? '-'
			: formatNumber(value, {style: 'currency', currency, maximumFractionDigits, currencyDisplay: 'narrowSymbol'});

	const projectBadge = (row: XeroExportLineRow): JSX.Element | null => {
		/*
		 * Through the shared helper, not `customProjectId` alone: only one of the two mappers feeding
		 * this table carries a custom id, so reading that field directly left legacy lines blank.
		 */
		const text = getProjectIndicatorString(row.project?.companyProjectId, row.customProjectId);
		return text ? <ColoredIdIcon color={row.projectColor ?? ''}>{text}</ColoredIdIcon> : null;
	};

	const heading = (label: string): JSX.Element => (
		<SubHeading size={'2'} noWrap ellipsis>
			{label}
		</SubHeading>
	);

	return (
		<LinesScroller>
			<LinesGrid role={'table'} $template={lineGridTemplate({showIdColumn, trackingCount: trackingCategories.length})}>
				<LinesRow role={'row'}>
					{showIdColumn && <LineCell role={'columnheader'}>{heading(idHeader)}</LineCell>}
					<LineCell role={'columnheader'}>{heading(formatMessage({id: 'common.project'}))}</LineCell>
					<LineCell role={'columnheader'} $maxWidth={LINE_DROPDOWN_MAX_PX.item}>
						{heading(formatMessage({id: 'integrations.xero.line.item'}))}
						{columnSizer(
							[itemPlaceholder, noneLabel],
							items.map(item => item.name),
							LINE_DROPDOWN_MAX_PX.item
						)}
					</LineCell>
					<LineCell role={'columnheader'}>{heading(formatMessage({id: 'common.description'}))}</LineCell>
					<LineCell role={'columnheader'} $maxWidth={LINE_DROPDOWN_MAX_PX.account}>
						{heading(formatMessage({id: 'integrations.xero.line.account'}))}
						{columnSizer(
							[accountPlaceholder],
							accounts.map(account => `${account.code} - ${account.name}`),
							LINE_DROPDOWN_MAX_PX.account
						)}
					</LineCell>
					<LineCell role={'columnheader'} $numeric>
						{heading(formatMessage({id: 'common.quantity'}))}
					</LineCell>
					<LineCell role={'columnheader'} $numeric>
						{heading(formatMessage({id: 'common.unit_price'}))}
					</LineCell>
					<LineCell role={'columnheader'} $numeric>
						{heading(formatMessage({id: 'project_section.xero_modal_table_discount'}))}
					</LineCell>
					<LineCell role={'columnheader'} $numeric>
						{heading(formatMessage({id: 'common.tax'}))}
					</LineCell>
					{trackingCategories.map(trackingCategory => (
						<LineCell
							key={`tracking-${trackingCategory.value}`}
							role={'columnheader'}
							$maxWidth={LINE_DROPDOWN_MAX_PX.tracking}
						>
							{heading(trackingCategory.name)}
							{columnSizer(
								[trackingPlaceholder(trackingCategory), noneLabel],
								trackingCategory.options.map(option => option.name),
								LINE_DROPDOWN_MAX_PX.tracking
							)}
						</LineCell>
					))}
					<LineCell role={'columnheader'} $numeric>
						{heading(formatMessage({id: 'expense_item_modal.total_price'}))}
					</LineCell>
				</LinesRow>

				{rows.map(row => (
					<LinesRow key={row.id} role={'row'}>
						{showIdColumn && <LineCell role={'cell'}>{projectBadge(row)}</LineCell>}
						<LineCell role={'cell'}>
							<FlexRow gap={'s'} alignItems={'center'}>
								{!showIdColumn && projectBadge(row)}
								<Text size={'2'} ellipsis tooltipOnEllipsis>
									{row.project?.name ?? ''}
								</Text>
							</FlexRow>
						</LineCell>
						<LineCell role={'cell'}>
							<FieldCell>
								<XeroLineDropdown
									value={shownValue(itemLabel(row), row, 'item')}
									placeholder={itemPlaceholder}
									options={itemOptions(row)}
									emptyMessage={emptyMessage(failed.items, 'integrations.xero.line.no_items')}
									disabled={fieldDisabled(failed.items)}
								/>
							</FieldCell>
						</LineCell>
						<LineCell role={'cell'}>
							<Text size={'2'} ellipsis>
								{row.entryDescription ?? ''}
							</Text>
						</LineCell>
						<LineCell role={'cell'}>
							<FieldCell>
								<XeroLineDropdown
									value={accountLabel(row) || undefined}
									placeholder={accountPlaceholder}
									options={accountOptions(row)}
									emptyMessage={emptyMessage(failed.accounts, 'integrations.xero.line.no_accounts')}
									disabled={fieldDisabled(failed.accounts)}
									errorText={
										accountInactive(row)
											? formatMessage({id: 'integrations.xero.line.account_inactive'})
											: undefined
									}
								/>
							</FieldCell>
						</LineCell>
						<LineCell role={'cell'} $numeric>
							<Numeral>{row.quantity === undefined ? '-' : formatNumber(row.quantity)}</Numeral>
						</LineCell>
						<LineCell role={'cell'} $numeric>
							<Numeral>{money(row.unitPrice, 4)}</Numeral>
						</LineCell>
						<LineCell role={'cell'} $numeric>
							<Numeral>
								{formatLinePercent(
									formatNumber,
									row.discountPercentage == null ? row.discountPercentage : row.discountPercentage * 100
								)}
							</Numeral>
						</LineCell>
						<LineCell role={'cell'} $numeric>
							<Numeral>{formatRate(rateFor(row))}</Numeral>
						</LineCell>
						{trackingCategories.map(trackingCategory => (
							<LineCell key={`tracking-${trackingCategory.value}`} role={'cell'}>
								<FieldCell>
									<XeroLineDropdown
										value={shownValue(
											trackingLabel(trackingCategory)(row),
											row,
											`tracking-${trackingCategory.value}`
										)}
										placeholder={trackingPlaceholder(trackingCategory)}
										options={trackingOptions(row, trackingCategory)}
										disabled={fieldDisabled(failed.trackingCategories)}
									/>
								</FieldCell>
							</LineCell>
						))}
						<LineCell role={'cell'} $numeric>
							<Numeral>{money(row.totalPrice)}</Numeral>
						</LineCell>
					</LinesRow>
				))}
			</LinesGrid>
		</LinesScroller>
	);
};

export default XeroExportLinesTable;
